import { Link } from 'react-router-dom';
import { Star, ArrowRight, CheckCircle, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Layout } from '../components/layout/Layout';

export default function Testimonials() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-slate-900 text-white" data-testid="testimonials-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-amber-500 font-medium mb-2">Real Results</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Client Success Stories
            </h1>
            <p className="text-lg text-slate-300">
              See how our clients have achieved their dreams of homeownership, vehicle ownership, 
              and financial freedom through our credit restoration services.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Testimonials */}
      <section className="py-24 bg-white" data-testid="featured-testimonials">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            
            {/* Lynn's Credit Transformation */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl overflow-hidden shadow-xl border border-slate-700">
              <div className="grid md:grid-cols-2">
                <div className="relative h-80 md:h-96 overflow-hidden bg-slate-800">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/8v2t7iuw_Lynn%20Car%20and%20Funding%20Testimony.PNG"
                    alt="Lynn's Success - New Infiniti & $20K Funding"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'center 40%', transform: 'scale(1.3)' }}
                  />
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center text-white">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      CREDIT RESTORED
                    </span>
                    <span className="bg-teal-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      $20K FUNDING
                    </span>
                    <span className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      NEW VEHICLE
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                    Lynn's Credit Transformation
                  </h3>
                  <Quote className="h-8 w-8 text-amber-500/50 mb-3" />
                  <p className="text-slate-300 text-base mb-6 italic">
                    "Artica, GOD is truly faithful and good. Due to all of your hard work and persistence, 
                    my credit score has allowed me to qualify for a $20,000 credit limit and the purchase of 
                    a 2023 QX60 with a low-interest rate, and most importantly, I didn't need a cosigner! 
                    I am FOREVER grateful!"
                  </p>
                  <div className="flex items-center gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white/10 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-amber-400">$20K</p>
                      <p className="text-xs text-slate-400">Credit Limit</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-teal-400">18%</p>
                      <p className="text-xs text-slate-400">APR Rate</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-blue-400">2023</p>
                      <p className="text-xs text-slate-400">QX60</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lynn's Airbnb Success */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl overflow-hidden shadow-xl border border-slate-700">
              <div className="grid md:grid-cols-2">
                <div className="p-8 md:p-10 flex flex-col justify-center text-white order-2 md:order-1">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="bg-teal-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      AIRBNB LAUNCHED
                    </span>
                    <span className="bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      BUSINESS OWNER
                    </span>
                    <span className="bg-slate-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      SUCCESS STORY
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                    Lynn's Airbnb Business Launch
                  </h3>
                  <Quote className="h-8 w-8 text-amber-500/50 mb-3" />
                  <p className="text-slate-300 text-base mb-6 italic">
                    "After restoring my credit with Tamperis Solutions, I was able to secure funding and launch 
                    my own Airbnb business! The coaching and guidance helped me understand how to leverage my 
                    improved credit to create a new stream of income. From faith to freedom - now I'm a business owner!"
                  </p>
                  <div className="flex items-center gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="relative h-24 rounded-lg overflow-hidden">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/rpmtx0tq_Lynn%20Inside.PNG"
                        alt="Modern Kitchen"
                        className="w-full h-full object-cover"
                        style={{ objectPosition: 'center 50%', transform: 'scale(1.5)' }}
                      />
                      <div className="absolute inset-0 bg-black/30"></div>
                      <p className="absolute bottom-2 left-2 text-xs text-white font-medium">Kitchen</p>
                    </div>
                    <div className="relative h-24 rounded-lg overflow-hidden">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/g4z9mfl0_Lynn%20Inside%201%20.PNG"
                        alt="Master Suite"
                        className="w-full h-full object-cover"
                        style={{ objectPosition: 'center 50%', transform: 'scale(1.5)' }}
                      />
                      <div className="absolute inset-0 bg-black/30"></div>
                      <p className="absolute bottom-2 left-2 text-xs text-white font-medium">Bedroom</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3 text-center flex flex-col justify-center">
                      <p className="text-lg font-bold text-teal-400">Airbnb</p>
                      <p className="text-xs text-slate-400">Now Live</p>
                    </div>
                  </div>
                </div>
                <div className="relative h-80 md:h-auto overflow-hidden order-1 md:order-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/emul7seb_Air%20b%26b%20Outside.PNG"
                    alt="Lynn's Airbnb Property"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'center 50%', transform: 'scale(1.5)' }}
                  />
                  <div className="absolute bottom-4 right-4">
                    <span className="bg-amber-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      PROPERTY EXTERIOR
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Homeownership Success */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl overflow-hidden shadow-xl border border-slate-200">
              <div className="grid md:grid-cols-2">
                <div className="relative h-80 md:h-96 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/wmz0k6a4_B%20House%20Outside.JPG"
                    alt="Client Success - New Homeowner"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'center 40%' }}
                  />
                  {/* Privacy overlay to blur address */}
                  <div 
                    className="absolute"
                    style={{
                      top: '15%',
                      left: '5%',
                      width: '25%',
                      height: '20%',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                      borderRadius: '8px',
                      background: 'rgba(255,255,255,0.1)'
                    }}
                  />
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="bg-teal-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      HOMEOWNER
                    </span>
                    <span className="bg-amber-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      $20K FUNDING
                    </span>
                    <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      SUCCESS
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                    Dream Home Achieved + $20K Funding
                  </h3>
                  <Quote className="h-8 w-8 text-amber-500/30 mb-3" />
                  <p className="text-slate-600 text-base mb-6 italic">
                    "Thanks to Tamperis Solutions, I was able to repair my credit, secure $20,000 in funding, 
                    and qualify for my dream home! The team guided me every step of the way. From faith to freedom - it really works! 
                    I never thought homeownership would be possible for me, but now I'm holding the keys to my future."
                  </p>
                  <div className="flex items-center gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                  <div className="flex items-center gap-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/howc33db_B%20House%20Inside.JPG"
                      alt="Closing Day"
                      className="w-20 h-20 rounded-lg object-cover border-2 border-amber-300"
                      style={{ objectPosition: 'center 30%' }}
                    />
                    <div>
                      <p className="font-semibold text-slate-900">Verified Client</p>
                      <p className="text-sm text-teal-600">Credit Restored • $20K Funded • Home Purchased</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Auto Loan Success */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl overflow-hidden shadow-xl border border-slate-200">
              <div className="grid md:grid-cols-2">
                <div className="p-8 md:p-10 flex flex-col justify-center order-2 md:order-1">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="bg-slate-700 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      AUTO LOAN
                    </span>
                    <span className="bg-amber-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      APPROVED
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                    Dream Car Financed
                  </h3>
                  <Quote className="h-8 w-8 text-amber-500/30 mb-3" />
                  <p className="text-slate-600 text-base mb-6 italic">
                    "After working with Tamperis Solutions, my credit score improved significantly. 
                    I was able to get approved for the car I always wanted with a great interest rate!
                    The process was smooth and the team kept me informed every step of the way."
                  </p>
                  <div className="flex items-center gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                  <div className="flex items-center gap-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/opg0d7u5_jap%20sign%20car.PNG"
                      alt="Signing Papers"
                      className="w-20 h-20 rounded-lg object-cover border-2 border-amber-300"
                      style={{ objectPosition: 'center center', transform: 'scale(1.3)' }}
                    />
                    <div>
                      <p className="font-semibold text-slate-900">Verified Client</p>
                      <p className="text-sm text-teal-600">Credit Restored • Auto Financed</p>
                    </div>
                  </div>
                </div>
                <div className="relative h-80 md:h-96 overflow-hidden order-1 md:order-2 bg-slate-100">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/u4qnuz0t_Jasper%20car.PNG"
                    alt="Client Success - New Vehicle"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Success Gallery */}
      <section className="py-24 bg-slate-50" data-testid="success-gallery">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              More Success Stories
            </h2>
            <p className="text-slate-600">
              Real results from real clients who trusted us with their credit restoration journey.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <div className="relative rounded-xl overflow-hidden shadow-lg group aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/howc33db_B%20House%20Inside.JPG"
                alt="Client closing on new home"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                style={{ objectPosition: 'center 30%' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white text-lg font-semibold">Home Closing Day</p>
                <p className="text-white/80 text-sm">Credit Restored Successfully</p>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden shadow-lg group aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/u4qnuz0t_Jasper%20car.PNG"
                alt="Client with new vehicle"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white text-lg font-semibold">New Vehicle Owner</p>
                <p className="text-white/80 text-sm">Auto Loan Approved</p>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden shadow-lg group aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_faith2freedom/artifacts/wmz0k6a4_B%20House%20Outside.JPG"
                alt="Client with new home"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                style={{ objectPosition: 'center 40%' }}
              />
              {/* Privacy overlay to blur address */}
              <div 
                className="absolute"
                style={{
                  top: '10%',
                  left: '5%',
                  width: '30%',
                  height: '25%',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderRadius: '6px',
                  background: 'rgba(255,255,255,0.1)'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white text-lg font-semibold">Dream Home + $20K Funding</p>
                <p className="text-white/80 text-sm">From Faith to Freedom</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900 text-white" data-testid="stats-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-heading text-4xl md:text-5xl font-bold text-amber-500">300+</p>
              <p className="text-slate-400 mt-2">Clients Served</p>
            </div>
            <div>
              <p className="font-heading text-4xl md:text-5xl font-bold text-teal-400">95%</p>
              <p className="text-slate-400 mt-2">Success Rate</p>
            </div>
            <div>
              <p className="font-heading text-4xl md:text-5xl font-bold text-amber-500">35</p>
              <p className="text-slate-400 mt-2">Day Update Cycle</p>
            </div>
            <div>
              <p className="font-heading text-4xl md:text-5xl font-bold text-teal-400">100%</p>
              <p className="text-slate-400 mt-2">FCRA Compliant</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white" data-testid="testimonials-cta">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            Join our growing list of satisfied clients who have transformed their financial future. 
            Your success story could be next.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/pricing">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 btn-active">
                Choose Your Package
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
